/*
 * Shopify Forms app embed — inject theme-matched styles into its shadow DOM.
 *
 * The app (<shopify-forms-embed>) renders in an OPEN shadow DOM and exposes most
 * of its look as :host CSS custom properties (handled declaratively in
 * snippets/shopify-forms-theme.liquid). A few things it does NOT expose need a
 * shadow-injected <style>, all referencing custom properties set on the host in
 * Liquid (custom properties cross the shadow boundary):
 *   1. the submit button's font-family / text-transform, so it matches the
 *      theme's .button (stable [data-testid="btn-form-submit"]);
 *   2. the inline form's width (the app centres a capped-width column);
 *   3. the fixed teaser/popup's bottom offset, so it can be lifted above the
 *      sticky add-to-cart bar (stable `.appEmbed` root class — the hashed
 *      `_appEmbed…` variants churn on app updates, the plain one does not).
 * No hashed class names, so it survives app updates.
 */
(function () {
  var RULE =
    // Submit button — match the theme's .button font + text-case.
    '[data-testid="btn-form-submit"]{' +
    'font-family:var(--forms-button-font,inherit)!important;' +
    'text-transform:var(--forms-button-text-case,none)!important;letter-spacing:normal!important;}' +
    // Min 16px on the fields so iOS Safari doesn't zoom in on focus.
    'input,textarea,select{font-size:16px!important;}' +
    // Fill the column without clipping: the app centres a capped-width inline
    // form and reserves an (empty) image column, so it floats narrow. Force the
    // form container full-width + single-column and drop the empty image cell.
    // box-sizing:border-box is what stops the right-edge clip — the app's
    // containers use content-box + padding, so a plain width:100% overflowed.
    // Targets the app's semantic class prefixes (stable across hash suffixes).
    '#app-embed,form,[class*="container"],[class*="inline"],[class*="grid"]{' +
    'width:100%!important;max-width:100%!important;box-sizing:border-box!important;}' +
    // At narrow breakpoints the app turns _container / _inline / _grid into
    // grids that CENTRE a fixed-width column (grid-template-columns:<fixed> +
    // justify-content/items:center) — the actual cause of the narrow inline form.
    // Force a single full-width track, stretched. (On non-grids these are inert.)
    // Excludes the POPUP subtree (`:not(:has(close button))`) so the app's own
    // image-right 2-column popup layout is left intact — only inline embeds are
    // collapsed to one column.
    '[class*="container"]:not(:has([class*="CloseButton"])),' +
    '[class*="inline"]:not(:has([class*="CloseButton"])),' +
    '[class*="grid"]:not(:has([class*="CloseButton"])){' +
    'grid-template-columns:1fr!important;justify-content:stretch!important;justify-items:stretch!important;}' +
    '[class*="imageLoading"]{display:none!important;}' +
    // Lead-capture POPUP (the "10% off" teaser) renders INSIDE this same shadow
    // root, so a theme {% style %} block (light DOM) can't reach it. Left alone —
    // and with the fill rules above also matching its `_formContainer` — the card
    // fills the screen. Cap + centre it with a gutter. The ONLY reliable popup
    // marker is the close (X) button: a popup card contains a
    // `_formContainerCloseButtonPosition` / `_formCloseButton` (both include the
    // substring "CloseButton"), an embedded form NEVER does — so `:has(close
    // button)` caps popups only and can't touch an inline embed (the app wraps
    // both in the same `_fadeAnimation`/`_container` shells, so ancestor-class
    // scoping wrongly hit embeds too). `:has()` contributes its arg's
    // specificity, so this is (0,2,0), beating the fill rules above (0,1,0).
    // `width:fit-content` makes the card hug its content (form + image); the
    // app's columns are content-sized justified to the start, so a stretched
    // `width:100%` left dead white space to the right of the image. max-width is
    // a ceiling only; `calc(100% - 32px)` keeps the mobile gutter.
    '[data-sizing="form-wrapper"]:has([class*="CloseButton"]),' +
    '[class*="formContainer"]:has([class*="CloseButton"]){' +
    'max-width:min(1000px,calc(100% - 32px))!important;' +
    'width:fit-content!important;margin-inline:auto!important;box-sizing:border-box!important;}' +
    // Lift the fixed teaser above the sticky add-to-cart bar. Reads the
    // --forms-teaser-offset custom property set on the host in Liquid (0 normally,
    // enough to clear the bar when it's stuck); custom properties cross the shadow boundary.
    '.appEmbed{bottom:var(--forms-teaser-offset,0px)!important;' +
    'transition:bottom .3s ease!important;}' +
    // The collapsed teaser root is a full-viewport-width fixed strip
    // (position:fixed; left:0; right:0), so its transparent area around the small
    // visible pill swallowed clicks on the footer/links beneath it. Make the
    // teaser container itself click-through and re-enable pointer-events only on
    // its interactive content (pointer-events inherits, so re-enabling the pill /
    // close button covers their inner text + icons). Scoped to the teaser (class
    // contains "Teaser"), so the expanded form/popup is untouched.
    '.appEmbed[class*="Teaser"]{pointer-events:none!important;}' +
    '.appEmbed[class*="Teaser"] a,.appEmbed[class*="Teaser"] button,' +
    '.appEmbed[class*="Teaser"] input,.appEmbed[class*="Teaser"] [role="button"],' +
    '.appEmbed[class*="Teaser"] [tabindex]{pointer-events:auto!important;}';

  function inject(root) {
    if (!root || root.querySelector('style[data-forms-theme]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-forms-theme', '');
    style.textContent = RULE;
    root.appendChild(style);
  }

  function wire(el) {
    if (el.__formsThemeWired || !el.shadowRoot) return;
    el.__formsThemeWired = true;
    inject(el.shadowRoot);
    // The app re-renders on step changes; re-inject if our <style> is dropped.
    new MutationObserver(function () {
      inject(el.shadowRoot);
    }).observe(el.shadowRoot, { childList: true });
  }

  function scan() {
    var els = document.querySelectorAll('shopify-forms-embed');
    for (var i = 0; i < els.length; i++) wire(els[i]);
    return els.length > 0 && Array.prototype.every.call(els, function (e) {
      return e.__formsThemeWired;
    });
  }

  // The embed hydrates (attaches its shadow root) asynchronously; poll briefly.
  var tries = 0;
  (function poll() {
    if (scan() || tries++ > 40) return;
    setTimeout(poll, 250);
  })();
})();
